import React, { useCallback, useMemo, useState } from 'react';

import { controlRole } from '../../common/actions/controlRole';
import useRequest from './useRequest';
import { fetchPaymentStatistics, fetchPeerCount, fetchPlayerStatistics, fetchUserStatistics, fetchVpnStatistics } from './api';
import { MONTHS, MONTHS_SHORT, formatCompact, formatCurrency, formatNumber, formatPercent, formatShortDate } from './format';
import { PRIMARY_COLOR } from './products';
import Filters, { presetRange } from './Filters';
import ColumnChart from './charts/ColumnChart';
import BarList from './charts/BarList';
import { ChartCard, DataTable, Kpi, Segmented } from './ui';
import './style.scss';

const sum = (items) => items.reduce((total, item) => total + item.value, 0);
const allZero = (series) => series.every(serie => serie.values.every(v => !v));

const VPN_FAMILIES = [{ value: 'vpnPass', label: 'VPN Pass' }, { value: 'vpnBox', label: 'VPN Box' }];

// tipo di licenza nei grafici player: 3 anni (default) o lifetime, ognuno con il suo colore
const LICENCE_TYPES = [{ value: 'threeYears', label: '3 anni' }, { value: 'lifetime', label: 'Lifetime' }];
const LICENCE_TYPE_INFO = {
    threeYears: { title: 'Licenze 3 anni', color: PRIMARY_COLOR },
    lifetime: { title: 'Licenze lifetime', color: '#1baf7a' }
};

const licenceSerie = (type, values) => [{ key: type, label: LICENCE_TYPE_INFO[type].title, color: LICENCE_TYPE_INFO[type].color, values }];

// tabella di una serie per categoria (giorni / mesi), una colonna per serie
function seriesTable(firstColumn, labels, series, format = formatNumber) {
    const columns = [{ key: 'label', label: firstColumn }, ...series.map(s => ({ key: s.key, label: series.length > 1 ? s.label : 'Totale', numeric: true, format }))];
    const rows = labels.map((label, i) => series.reduce((row, s) => ({ ...row, [s.key]: s.values[i] || 0 }), { key: label, label }));
    return <DataTable columns={columns} rows={rows} />;
}

function itemsTable(firstColumn, items, format = formatNumber, series) {
    const columns = [
        { key: 'label', label: firstColumn },
        ...(series || []).map(s => ({ key: s.key, label: s.label, numeric: true, format })),
        { key: 'value', label: 'Totale', numeric: true, format }
    ];
    const rows = items.map(item => ({
        ...item,
        ...(item.segments || []).reduce((acc, segment) => ({ ...acc, [segment.key]: segment.value }), {})
    }));
    return <DataTable columns={columns} rows={rows} />;
}

/**
 * Sezione statistiche della dashboard.
 * Le chiamate partono solo per le statistiche che l'utente può vedere; ogni grafico gestisce il proprio stato di caricamento.
 */
export default function Statistics({ role }) {
    const can = useMemo(() => ({
        users: controlRole(role.user, 'api/User/Statistics', 'GET'),
        player: controlRole(role.player, 'api/Player/Statistics', 'GET'),
        payment: controlRole(role.payment, 'api/Payment/Statistics', 'GET'),
        vpn: controlRole(role.vpnPass, 'api/VpnPass/Statistics', 'GET'),
        peer: controlRole(role.vpnPeer, 'api/VpnPeer', 'GET'),
    }), [role]);

    const today = new Date();
    const years = [today.getFullYear() - 2, today.getFullYear() - 1, today.getFullYear()];

    const [filters, setFilters] = useState(() => ({
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: 0,
        ...presetRange('month', today)
    }));
    const { year, month, day, from, to } = filters;

    // viste interne alle card
    const [platformView, setPlatformView] = useState('range');
    const [dayLicenceType, setDayLicenceType] = useState('threeYears');
    const [monthLicenceType, setMonthLicenceType] = useState('threeYears');
    const [platformLicenceType, setPlatformLicenceType] = useState('threeYears');
    const [gainsView, setGainsView] = useState('gross');
    const [vpnMonthFamily, setVpnMonthFamily] = useState('vpnPass');
    const [vpnDayFamily, setVpnDayFamily] = useState('vpnPass');
    const [updatedAt, setUpdatedAt] = useState(() => new Date());

    const fetchUsers = useCallback((params, signal) => fetchUserStatistics(signal), []);
    const fetchPeers = useCallback((params, signal) => fetchPeerCount(signal), []);

    const users = useRequest(fetchUsers, null, can.users);
    const peers = useRequest(fetchPeers, null, can.peer);
    const player = useRequest(fetchPlayerStatistics, { year, month, from, to }, can.player);
    const payments = useRequest(fetchPaymentStatistics, { year, month, day, from, to }, can.payment);
    const vpn = useRequest(fetchVpnStatistics, { year, month }, can.vpn);

    const reloadAll = () => {
        [users, peers, player, payments, vpn].forEach(request => request.reload());
        setUpdatedAt(new Date());
    };

    const monthLabel = `${MONTHS[month - 1]} ${year}`;
    const rangeLabel = from === to ? formatShortDate(from) : `${formatShortDate(from)} – ${formatShortDate(to)}`;

    const p = player.data;
    const pay = payments.data;
    const v = vpn.data;

    const dayCategories = p ? p.byDay.threeYears.map((_, i) => String(i + 1)) : [];
    const dayTooltips = dayCategories.map(d => `${d} ${monthLabel}`);
    const monthTooltips = MONTHS.map(m => `${m} ${year}`);

    const licencesByDay = p ? licenceSerie(dayLicenceType, p.byDay[dayLicenceType]) : [];
    const licencesByMonth = p ? licenceSerie(monthLicenceType, p.byMonth[monthLicenceType]) : [];
    const platformShowsTotal = platformView === 'total';
    const platforms = p ? (platformShowsTotal ? p.platformsTotal : p.platformsRange)[platformLicenceType] : [];
    const gains = pay ? (gainsView === 'gross' ? pay.grossGains : pay.netGains) : [];
    const countries = pay ? (day ? pay.countriesDay : pay.countriesMonth) : [];
    const vpnByMonth = v ? v.byMonth[vpnMonthFamily] : [];
    const vpnByDay = v ? v.byDay[vpnDayFamily] : [];
    const vpnDayCategories = vpnByDay.length ? vpnByDay[0].values.map((_, i) => String(i + 1)) : dayCategories;

    const vpnActiveTotal = v ? sum(v.active) : undefined;

    return (
        <div className="statistics">
            <div className="stPageHeader">
                <div>
                    <h2>Statistiche</h2>
                    <p>Dati aggiornati alle {updatedAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} del {updatedAt.toLocaleDateString('it-IT')}</p>
                </div>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={reloadAll}>
                    <i className="fas fa-sync-alt" /> Aggiorna
                </button>
            </div>

            <Filters filters={filters} onChange={setFilters} years={years} />

            {/* ------------------------------ KPI ------------------------------ */}
            <div className="stKpis">
                {
                    can.users &&
                    <Kpi
                        label="Utenti registrati"
                        value={users.data ? formatCompact(users.data.total) : undefined}
                        hint={users.data && `${formatNumber(users.data.confirmed)} confermati (${formatPercent(users.data.confirmed, users.data.total)})`}
                        loading={users.loading}
                        error={users.error}
                    />
                }
                {can.player && <Kpi label="Player totali" value={p ? formatCompact(p.totalPlayers) : undefined} loading={player.loading} error={player.error} />}
                {can.player && <Kpi label="Licenze 3 anni" value={p ? formatCompact(p.totalThreeYears) : undefined} loading={player.loading} error={player.error} />}
                {can.player && <Kpi label="Licenze lifetime" value={p ? formatCompact(p.totalLifetime) : undefined} loading={player.loading} error={player.error} />}
                {can.payment && <Kpi label="Pagamenti" value={pay ? formatCompact(sum(pay.providers)) : undefined} hint="tutti i provider" loading={payments.loading} error={payments.error} />}
                {can.vpn && <Kpi label="VPN attive" value={v ? formatCompact(vpnActiveTotal) : undefined} loading={vpn.loading} error={vpn.error} />}
                {can.peer && <Kpi label="VPN Peer" value={peers.data !== null ? formatCompact(peers.data) : undefined} loading={peers.loading} error={peers.error} />}
            </div>

            {/* ------------------------------ PLAYER ------------------------------ */}
            {
                can.player &&
                <section className="stSection">
                    <h4 className="stSectionTitle">Player</h4>
                    <div className="stGrid">
                        <ChartCard
                            title={`${LICENCE_TYPE_INFO[dayLicenceType].title} attivate per giorno`}
                            subtitle={monthLabel}
                            actions={<Segmented label="Tipo di licenza al giorno" value={dayLicenceType} onChange={setDayLicenceType} options={LICENCE_TYPES} />}
                            request={player}
                            hasData={!!p}
                            isEmpty={p && allZero(licencesByDay)}
                            renderTable={() => seriesTable('Giorno', dayTooltips, licencesByDay)}
                        >
                            <ColumnChart categories={dayCategories} tooltipLabels={dayTooltips} series={licencesByDay} />
                        </ChartCard>

                        <ChartCard
                            title={`${LICENCE_TYPE_INFO[monthLicenceType].title} attivate per mese`}
                            subtitle={String(year)}
                            actions={<Segmented label="Tipo di licenza al mese" value={monthLicenceType} onChange={setMonthLicenceType} options={LICENCE_TYPES} />}
                            request={player}
                            hasData={!!p}
                            isEmpty={p && allZero(licencesByMonth)}
                            renderTable={() => seriesTable('Mese', monthTooltips, licencesByMonth)}
                        >
                            <ColumnChart categories={MONTHS_SHORT} tooltipLabels={monthTooltips} series={licencesByMonth} />
                        </ChartCard>

                        <ChartCard
                            title={`${LICENCE_TYPE_INFO[platformLicenceType].title} per piattaforma`}
                            subtitle={platformShowsTotal ? 'Totale licenze' : `Attivate nel periodo ${rangeLabel}`}
                            actions={
                                <>
                                    <Segmented label="Tipo di licenza per piattaforma" value={platformLicenceType} onChange={setPlatformLicenceType} options={LICENCE_TYPES} />
                                    <Segmented label="Periodo piattaforme" value={platformView} onChange={setPlatformView} options={[{ value: 'range', label: 'Periodo' }, { value: 'total', label: 'Totale' }]} />
                                </>
                            }
                            request={player}
                            hasData={!!p}
                            isEmpty={p && sum(platforms) === 0}
                            renderTable={() => itemsTable('Piattaforma', platforms)}
                            minHeight={200}
                        >
                            <BarList items={platforms} color={LICENCE_TYPE_INFO[platformLicenceType].color} />
                        </ChartCard>
                    </div>
                </section>
            }

            {/* ------------------------------ PAGAMENTI ------------------------------ */}
            {
                can.payment &&
                <section className="stSection">
                    <h4 className="stSectionTitle">Pagamenti</h4>
                    <div className="stGrid">
                        <ChartCard
                            title={gainsView === 'gross' ? 'Ricavi lordi per prodotto' : 'Ricavi netti per prodotto'}
                            subtitle={pay ? `${formatCurrency(sum(gains))} · ${rangeLabel}` : rangeLabel}
                            actions={<Segmented label="Tipo di ricavo" value={gainsView} onChange={setGainsView} options={[{ value: 'gross', label: 'Lordi' }, { value: 'net', label: 'Netti' }]} />}
                            request={payments}
                            hasData={!!pay}
                            isEmpty={pay && gains.length === 0}
                            renderTable={() => itemsTable('Prodotto', gains, formatCurrency)}
                        >
                            <BarList items={gains} formatValue={formatCurrency} />
                        </ChartCard>

                        <ChartCard
                            title="Pagamenti per provider"
                            subtitle={pay ? `${formatNumber(sum(pay.providers))} pagamenti` : undefined}
                            request={payments}
                            hasData={!!pay}
                            isEmpty={pay && sum(pay.providers) === 0}
                            renderTable={() => itemsTable('Provider', pay.providers)}
                            minHeight={200}
                        >
                            {pay && <BarList items={pay.providers} />}
                        </ChartCard>

                        <ChartCard
                            title="Pagamenti per paese"
                            subtitle={day ? `${day} ${monthLabel}` : monthLabel}
                            request={payments}
                            hasData={!!pay}
                            isEmpty={pay && countries.length === 0}
                            renderTable={() => itemsTable('Paese', countries)}
                        >
                            <BarList items={countries} />
                        </ChartCard>
                    </div>
                </section>
            }

            {/* ------------------------------ VPN ------------------------------ */}
            {
                (can.vpn || can.payment) &&
                <section className="stSection">
                    <h4 className="stSectionTitle">VPN</h4>
                    <div className="stGrid">
                        {
                            can.vpn &&
                            <ChartCard
                                title="VPN attivate per mese"
                                subtitle={String(year)}
                                actions={<Segmented label="Tipo VPN mensile" value={vpnMonthFamily} onChange={setVpnMonthFamily} options={VPN_FAMILIES} />}
                                request={vpn}
                                hasData={!!v}
                                isEmpty={v && allZero(vpnByMonth)}
                                renderTable={() => seriesTable('Mese', monthTooltips, vpnByMonth)}
                            >
                                <ColumnChart categories={MONTHS_SHORT} tooltipLabels={monthTooltips} series={vpnByMonth} />
                            </ChartCard>
                        }

                        {
                            can.vpn &&
                            <ChartCard
                                title="VPN attivate per giorno"
                                subtitle={monthLabel}
                                actions={<Segmented label="Tipo VPN giornaliero" value={vpnDayFamily} onChange={setVpnDayFamily} options={VPN_FAMILIES} />}
                                request={vpn}
                                hasData={!!v}
                                isEmpty={v && allZero(vpnByDay)}
                                renderTable={() => seriesTable('Giorno', vpnDayCategories.map(d => `${d} ${monthLabel}`), vpnByDay)}
                            >
                                <ColumnChart categories={vpnDayCategories} tooltipLabels={vpnDayCategories.map(d => `${d} ${monthLabel}`)} series={vpnByDay} />
                            </ChartCard>
                        }

                        {
                            can.payment &&
                            <ChartCard
                                title="VPN Pass venduti per paese"
                                subtitle={rangeLabel}
                                request={payments}
                                hasData={!!pay}
                                isEmpty={pay && pay.vpnByCountry.items.length === 0}
                                renderTable={() => itemsTable('Paese', pay.vpnByCountry.items, formatNumber, pay.vpnByCountry.series)}
                            >
                                {pay && <BarList items={pay.vpnByCountry.items} series={pay.vpnByCountry.series} />}
                            </ChartCard>
                        }

                        {
                            can.vpn &&
                            <ChartCard
                                title="VPN attive per prodotto"
                                subtitle={v ? `${formatNumber(vpnActiveTotal)} attive` : undefined}
                                request={vpn}
                                hasData={!!v}
                                isEmpty={v && vpnActiveTotal === 0}
                                renderTable={() => itemsTable('Prodotto', v.active)}
                                minHeight={200}
                            >
                                {v && <BarList items={v.active} limit={12} />}
                            </ChartCard>
                        }
                    </div>
                </section>
            }
        </div>
    );
}
